/**
 * Design-system aligned UI primitives — antd-compatible API surface.
 * Imported via Vite alias: `antd` -> `@/shared/ui/primitives`.
 */
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  Fragment,
} from 'react';
import type {
  CSSProperties,
  ChangeEvent,
  FormEvent,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import {
  CloseOutlined,
  DownOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
} from './icons';

/* ========================================================================
   Utilities
   ======================================================================== */
const cx = (...c: any[]) => c.filter(Boolean).join(' ');
const omit = <T extends Record<string, any>>(obj: T, keys: string[]): Partial<T> => {
  const r: any = {};
  for (const k in obj) if (!keys.includes(k)) r[k] = obj[k];
  return r;
};
const SENSITIVE_FIELD_PATTERN = /\b(password|passcode|otp|token|secret|email|phone|mobile|whatsapp|national|nationalid|national_id|card|payment|iban|idnumber)\b/i;
function allStakPrivacyAttrs(props: Record<string, any>, type?: string) {
  const haystack = [
    type,
    props.name,
    props.id,
    props.autoComplete,
    props.placeholder,
    props['aria-label'],
  ].filter(Boolean).join(' ');
  if (!SENSITIVE_FIELD_PATTERN.test(haystack)) return {};
  return {
    'data-allstak-mask': props['data-allstak-mask'] ?? 'true',
    'data-sensitive': props['data-sensitive'] ?? 'true',
  };
}

/* ========================================================================
   Button
   ======================================================================== */
export interface ButtonProps {
  type?: 'default' | 'primary' | 'dashed' | 'text' | 'link' | 'ghost';
  htmlType?: 'button' | 'submit' | 'reset';
  danger?: boolean;
  size?: 'small' | 'middle' | 'large';
  icon?: ReactNode;
  iconPosition?: 'start' | 'end';
  loading?: boolean;
  block?: boolean;
  disabled?: boolean;
  shape?: 'default' | 'circle' | 'round';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  target?: string;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  title?: string;
  id?: string;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { type = 'default', htmlType, danger, size = 'middle', icon, iconPosition = 'start', loading, block, disabled, shape, onClick, href, target, children, className, style, title, id },
  ref,
) {
  const cls = cx(
    'ds-btn',
    type === 'primary' && 'ds-btn--primary',
    type === 'text' && 'ds-btn--text',
    type === 'link' && 'ds-btn--link',
    type === 'ghost' && 'ds-btn--ghost',
    type === 'dashed' && 'ds-btn--dashed',
    danger && 'ds-btn--danger',
    size === 'small' && 'ds-btn--sm',
    size === 'large' && 'ds-btn--lg',
    block && 'ds-btn--block',
    loading && 'ds-btn--loading',
    !children && icon && 'ds-btn--icon-only',
    shape === 'circle' && 'ds-btn--circle',
    className,
  );
  const content = (
    <>
      {loading && <span className="ds-spin ds-spin-sm"><span className="ds-spin-dot" /></span>}
      {icon && iconPosition === 'start' && !loading && icon}
      {children}
      {icon && iconPosition === 'end' && icon}
    </>
  );
  if (href) {
    return (
      <a ref={ref as any} id={id} href={href} target={target} className={cls} style={style} title={title} onClick={onClick as any} aria-disabled={disabled}>
        {content}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      id={id}
      type={htmlType || 'button'}
      className={cls}
      style={style}
      title={title}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {content}
    </button>
  );
});

/* ========================================================================
   Input / Textarea / Password / Search
   ======================================================================== */
interface BaseInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size' | 'onChange'> {
  [key: `data-${string}`]: string | boolean | undefined;
  prefix?: ReactNode;
  suffix?: ReactNode;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  value?: any;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  status?: 'error' | 'warning';
  addonAfter?: ReactNode;
  addonBefore?: ReactNode;
  bordered?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onPressEnter?: (e: any) => void;
}

export const Input = forwardRef<HTMLInputElement, BaseInputProps>(function Input(
  { prefix, suffix, allowClear, size, status, addonAfter, addonBefore, className, style, value, onChange, disabled, readOnly, onPressEnter, onKeyDown, ...rest },
  ref,
) {
  const privacyAttrs = allStakPrivacyAttrs(rest as Record<string, any>, (rest as Record<string, any>).type);
  const hasAffix = prefix || suffix || allowClear;
  const showClear = allowClear && value;
  const cls = cx('ds-input-wrap', status === 'error' && 'ds-input-error', className);
  const triggerChange = (val: string) => {
    const ev = { target: { value: val } } as ChangeEvent<HTMLInputElement>;
    onChange?.(ev);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onPressEnter?.(e);
    onKeyDown?.(e);
  };
  if (!hasAffix) {
    return (
      <input
        ref={ref}
        className={cx('ds-input', status === 'error' && 'ds-input-error', className)}
        style={style}
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        onKeyDown={handleKeyDown}
        {...rest}
        {...privacyAttrs}
      />
    );
  }
  return (
    <div className={cls} style={style}>
      {prefix && <span className="ds-affix">{prefix}</span>}
      <input
        ref={ref}
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        onKeyDown={handleKeyDown}
        {...rest}
        {...privacyAttrs}
      />
      {showClear && (
        <button type="button" className="ds-affix" style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: 2 }} onClick={() => triggerChange('')} aria-label="clear">
          <CloseCircleOutlined />
        </button>
      )}
      {suffix && <span className="ds-affix">{suffix}</span>}
    </div>
  );
}) as any;

const InputPassword = forwardRef<HTMLInputElement, BaseInputProps>(function InputPassword(props, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      {...props}
      ref={ref}
      type={visible ? 'text' : 'password'}
      autoComplete={props.autoComplete ?? 'current-password'}
      data-allstak-mask="true"
      data-sensitive="true"
      suffix={
        <button type="button" onClick={() => setVisible((v) => !v)} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }} aria-label="toggle password">
          <EyeOutlined />
        </button>
      }
    />
  );
});

const InputSearch = forwardRef<HTMLInputElement, BaseInputProps & { onSearch?: (v: string) => void; enterButton?: ReactNode }>(function InputSearch(
  { onSearch, enterButton, value, onChange, placeholder, ...rest },
  ref,
) {
  const [inner, setInner] = useState<string>((value as string) ?? '');
  useEffect(() => { if (value !== undefined) setInner(String(value)); }, [value]);
  const handle = () => onSearch?.(inner);
  return (
    <div className="ds-input-wrap ds-input-search">
      <span className="ds-affix"><SearchOutlined /></span>
      <input
        ref={ref}
        value={inner}
        onChange={(e) => { setInner(e.target.value); onChange?.(e); }}
        onKeyDown={(e) => { if (e.key === 'Enter') handle(); }}
        placeholder={placeholder}
        {...(rest as any)}
      />
      {enterButton && (
        <Button size="small" type="primary" onClick={handle}>
          {typeof enterButton === 'boolean' ? 'Search' : enterButton}
        </Button>
      )}
    </div>
  );
});

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  [key: `data-${string}`]: string | boolean | undefined;
  value?: any;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  showCount?: boolean;
  status?: 'error' | 'warning';
}
const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { className, style, value, onChange, rows = 3, status, showCount, maxLength, ...rest },
  ref,
) {
  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={ref}
        className={cx('ds-textarea', status === 'error' && 'ds-input-error', className)}
        style={style}
        rows={rows}
        value={value ?? ''}
        onChange={onChange}
        maxLength={maxLength}
        {...rest}
        data-allstak-mask={(rest as Record<string, any>)['data-allstak-mask'] ?? 'true'}
        data-sensitive={(rest as Record<string, any>)['data-sensitive'] ?? 'true'}
      />
      {showCount && maxLength && (
        <span style={{ position: 'absolute', bottom: 6, insetInlineEnd: 10, fontSize: 11, color: 'var(--text-tertiary)' }}>
          {String(value ?? '').length}/{maxLength}
        </span>
      )}
    </div>
  );
});

(Input as any).Password = InputPassword;
(Input as any).Search = InputSearch;
(Input as any).TextArea = TextArea;

/* ========================================================================
   InputNumber
   ======================================================================== */
export interface InputNumberProps {
  value?: number | string | null;
  onChange?: (val: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  addonAfter?: ReactNode;
  addonBefore?: ReactNode;
  status?: 'error' | 'warning';
  precision?: number;
}
export function InputNumber({ value, onChange, min, max, step = 1, placeholder, disabled, className, style, status, precision }: InputNumberProps) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));
  const [focused, setFocused] = useState(false);
  // Sync from the controlled value only when NOT actively editing, so an in-progress
  // entry (e.g. typing "2" on the way to "26") is never clobbered or clamped mid-stroke.
  useEffect(() => { if (!focused) setText(value == null ? '' : String(value)); }, [value, focused]);
  // While typing, accept the raw input and report the parsed number WITHOUT clamping —
  // clamping to min/max on every keystroke makes fields with a min (e.g. age min=16)
  // impossible to fill manually.
  const handleChange = (raw: string) => {
    setText(raw);
    if (raw === '' || raw === '-') { onChange?.(null); return; }
    let n = Number(raw);
    if (Number.isNaN(n)) return;
    if (precision !== undefined) n = Number(n.toFixed(precision));
    onChange?.(n);
  };
  // Clamp to [min, max] and normalize the text only once the user leaves the field.
  const handleBlur = () => {
    setFocused(false);
    if (text === '' || text === '-') { onChange?.(null); return; }
    let n = Number(text);
    if (Number.isNaN(n)) { setText(value == null ? '' : String(value)); return; }
    if (precision !== undefined) n = Number(n.toFixed(precision));
    if (min !== undefined && n < min) n = min;
    if (max !== undefined && n > max) n = max;
    setText(String(n));
    onChange?.(n);
  };
  return (
    <div className={cx('ds-input-wrap ds-input-number', status === 'error' && 'ds-input-error', className)} style={style}>
      <input
        type="number"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}

/* ========================================================================
   Select
   ======================================================================== */
export interface SelectOption {
  value: any;
  label: ReactNode;
  disabled?: boolean;
  [k: string]: any;
}
export interface SelectProps {
  value?: any;
  defaultValue?: any;
  onChange?: (val: any, option?: SelectOption | SelectOption[]) => void;
  options?: SelectOption[];
  placeholder?: ReactNode;
  allowClear?: boolean;
  showSearch?: boolean;
  mode?: 'multiple' | 'tags';
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  dropdownStyle?: CSSProperties;
  popupMatchSelectWidth?: boolean;
  filterOption?: boolean | ((input: string, option: SelectOption) => boolean);
  onSearch?: (v: string) => void;
  onSelect?: (val: any) => void;
  onDeselect?: (val: any) => void;
  onClear?: () => void;
  labelInValue?: boolean;
  notFoundContent?: ReactNode;
  size?: 'small' | 'middle' | 'large';
  status?: 'error' | 'warning';
  children?: ReactNode;
  onBlur?: () => void;
  onFocus?: () => void;
  dropdownRender?: (menu: ReactNode) => ReactNode;
  popupRender?: (menu: ReactNode) => ReactNode;
  optionFilterProp?: string;
  optionLabelProp?: string;
  maxTagCount?: number | 'responsive';
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
  virtual?: boolean;
  autoFocus?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPopupScroll?: (e: any) => void;
  id?: string;
}
function SelectImpl(props: SelectProps) {
  const {
    value, defaultValue, onChange, options: optionsProp, placeholder, allowClear, showSearch, mode, loading, disabled,
    className, style, filterOption = true, onSearch, onSelect, onDeselect, onClear, notFoundContent, status, children,
    onBlur, onFocus, onPopupScroll, open: openProp, onOpenChange, id,
  } = props;

  const isControlled = value !== undefined;
  const [inner, setInner] = useState<any>(defaultValue ?? (mode ? [] : undefined));
  // When the controlled value changes (including transitions to undefined / null on reset),
  // keep inner in sync so re-mounts or toggled-controlled states show the correct value.
  useEffect(() => {
    if (value !== undefined) setInner(value);
    else setInner(mode ? [] : undefined);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = isControlled ? value : inner;
  const [open, setOpen] = useState(false);
  const effectiveOpen = openProp !== undefined ? openProp : open;
  const setOpenBoth = (o: boolean) => { setOpen(o); onOpenChange?.(o); };
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // The dropdown is portaled to <body> so it is never clipped by an ancestor with
  // overflow (e.g. inside a Modal). Track the trigger rect to position it.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Extract options from children if not provided
  const options: SelectOption[] = useMemo(() => {
    if (optionsProp && optionsProp.length) return optionsProp;
    const arr: SelectOption[] = [];
    const walk = (nodes: ReactNode) => {
      if (!nodes) return;
      const children = Array.isArray(nodes) ? nodes : [nodes];
      for (const n of children) {
        if (isValidElement(n)) {
          const p = n.props as any;
          if (p.value !== undefined) arr.push({ value: p.value, label: p.children ?? p.label, disabled: p.disabled });
          else if (p.children) walk(p.children);
        }
      }
    };
    walk(children);
    return arr;
  }, [optionsProp, children]);

  useEffect(() => {
    if (!effectiveOpen) { setMenuPos(null); return; }
    const updatePos = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom, left: r.left, width: r.width });
    };
    updatePos();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      const inWrap = wrapRef.current?.contains(t);
      const inMenu = menuRef.current?.contains(t);
      if (!inWrap && !inMenu) setOpenBoth(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [effectiveOpen]);

  const filtered = useMemo(() => {
    if (!query || !showSearch) return options;
    if (filterOption === false) return options;
    if (typeof filterOption === 'function') return options.filter((o) => filterOption(query, o));
    const q = query.toLowerCase();
    return options.filter((o) => String(o.label ?? o.value).toLowerCase().includes(q));
  }, [options, query, showSearch, filterOption]);

  const multiValues: any[] = mode ? (Array.isArray(val) ? val : []) : [];
  const labelForValue = (v: any) => options.find((o) => o.value === v)?.label ?? v;

  const pick = (opt: SelectOption) => {
    if (opt.disabled) return;
    if (mode) {
      const exists = multiValues.includes(opt.value);
      const next = exists ? multiValues.filter((x) => x !== opt.value) : [...multiValues, opt.value];
      if (!isControlled) setInner(next);
      onChange?.(next);
      if (exists) onDeselect?.(opt.value); else onSelect?.(opt.value);
    } else {
      if (!isControlled) setInner(opt.value);
      onChange?.(opt.value, opt);
      onSelect?.(opt.value);
      setOpenBoth(false);
    }
    setQuery('');
  };

  const clear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!isControlled) setInner(mode ? [] : undefined);
    onChange?.(mode ? [] : undefined);
    onClear?.();
  };

  const hasValue = mode ? multiValues.length > 0 : val !== undefined && val !== null && val !== '';

  return (
    <div className={cx('ds-select', className)} style={style} ref={wrapRef} id={id}>
      <div
        className={cx('ds-select-inner', effectiveOpen && 'ds-select-open', status === 'error' && 'ds-input-error')}
        onClick={() => { if (!disabled) { setOpenBoth(!effectiveOpen); onFocus?.(); } }}
        role="combobox"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onBlur={onBlur}
      >
        {mode ? (
          <div className="ds-select-val ds-select-tags">
            {multiValues.length === 0 && <span className="ds-select-ph">{placeholder}</span>}
            {multiValues.map((v) => (
              <span key={v} className="ds-select-tag">
                {labelForValue(v)}
                <button type="button" onClick={(e) => { e.stopPropagation(); pick({ value: v, label: '' }); }} aria-label="remove">
                  <CloseOutlined />
                </button>
              </span>
            ))}
          </div>
        ) : hasValue ? (
          <span className="ds-select-val">{labelForValue(val)}</span>
        ) : (
          <span className="ds-select-val ds-select-ph">{placeholder}</span>
        )}
      </div>
      {allowClear && hasValue && !disabled && (
        <button type="button" className="ds-select-clear" onClick={clear} aria-label="clear"><CloseCircleOutlined /></button>
      )}
      <span className="ds-select-caret"><DownOutlined /></span>
      {effectiveOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          className="ds-select-menu ds-select-menu--portal"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, minWidth: menuPos.width, zIndex: 100001 }}
          onScroll={onPopupScroll}
        >
          {showSearch && (
            <div className="ds-select-search">
              <input
                autoFocus
                value={query}
                onChange={(e) => { setQuery(e.target.value); onSearch?.(e.target.value); }}
                placeholder="Search..."
              />
            </div>
          )}
          {loading ? (
            <div className="ds-select-empty"><span className="ds-spin ds-spin-sm"><span className="ds-spin-dot" /></span></div>
          ) : filtered.length === 0 ? (
            <div className="ds-select-empty">{notFoundContent ?? 'No data'}</div>
          ) : (
            filtered.map((o) => {
              const selected = mode ? multiValues.includes(o.value) : o.value === val;
              return (
                <div
                  key={String(o.value)}
                  className={cx('ds-select-opt', selected && 'selected', o.disabled && 'disabled')}
                  onClick={() => pick(o)}
                >
                  {o.label}
                </div>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
(SelectImpl as any).Option = function Option({ value, children, disabled, label }: { value: any; children?: ReactNode; disabled?: boolean; label?: ReactNode }) {
  return null as any;
};

/* ========================================================================
   Switch
   ======================================================================== */
export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'default' | 'small';
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
}
export function Switch({ checked, defaultChecked, onChange, disabled, size, className, style }: SwitchProps) {
  const isControlled = checked !== undefined;
  const [inner, setInner] = useState(!!defaultChecked);
  const val = isControlled ? !!checked : inner;
  const toggle = () => {
    if (disabled) return;
    const next = !val;
    if (!isControlled) setInner(next);
    onChange?.(next);
  };
  return (
    <label className={cx('ds-switch', size === 'small' && 'ds-switch-sm', className)} style={style}>
      <input type="checkbox" checked={val} onChange={toggle} disabled={disabled} />
      <span className="ds-switch-track" />
      <span className="ds-switch-thumb" />
    </label>
  );
}

/* ========================================================================
   Checkbox / Radio
   ======================================================================== */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: any;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (e: { target: { checked: boolean; value?: any } }) => void;
  children?: ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
}
function CheckboxImpl({ checked, defaultChecked, onChange, children, disabled, value, ...rest }: CheckboxProps) {
  const isControlled = checked !== undefined;
  const [inner, setInner] = useState(!!defaultChecked);
  const v = isControlled ? !!checked : inner;
  const toggle = () => {
    if (disabled) return;
    const next = !v;
    if (!isControlled) setInner(next);
    onChange?.({ target: { checked: next, value } });
  };
  return (
    <label className="ds-check">
      <input type="checkbox" checked={v} onChange={toggle} disabled={disabled} {...(rest as any)} />
      <span className="ds-check-box" />
      {children}
    </label>
  );
}
function CheckboxGroup({ options = [], value = [], onChange }: { options?: Array<{ label: ReactNode; value: any } | string>; value?: any[]; onChange?: (v: any[]) => void }) {
  const set = new Set(value);
  const toggle = (v: any) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange?.(Array.from(next));
  };
  return (
    <div style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap' }}>
      {options.map((opt) => {
        const o = typeof opt === 'string' ? { label: opt, value: opt } : opt;
        return <Checkbox key={String(o.value)} checked={set.has(o.value)} onChange={() => toggle(o.value)}>{o.label}</Checkbox>;
      })}
    </div>
  );
}
export const Checkbox = Object.assign(CheckboxImpl, { Group: CheckboxGroup });

export interface RadioProps {
  value?: any;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (e: { target: { value: any; checked: boolean } }) => void;
  children?: ReactNode;
  disabled?: boolean;
}
const RadioGroupCtx = createContext<{ value: any; setValue: (v: any) => void } | null>(null);
function RadioImpl({ checked, defaultChecked, onChange, children, disabled, value }: RadioProps) {
  const isControlled = checked !== undefined;
  const [inner, setInner] = useState(!!defaultChecked);
  const ctx = useContext(RadioGroupCtx);
  const v = ctx ? ctx.value === value : isControlled ? !!checked : inner;
  const handle = () => {
    if (disabled) return;
    if (ctx) { ctx.setValue(value); return; }
    if (!isControlled) setInner(true);
    onChange?.({ target: { value, checked: true } });
  };
  return (
    <label className="ds-radio">
      <input type="radio" checked={v} onChange={handle} disabled={disabled} value={value} />
      <span className="ds-radio-box" />
      {children}
    </label>
  );
}
function RadioGroup({ value, defaultValue, onChange, options, children, className, style }: { value?: any; defaultValue?: any; onChange?: (e: { target: { value: any } }) => void; options?: Array<{ label: ReactNode; value: any; disabled?: boolean } | string>; children?: ReactNode; className?: string; style?: CSSProperties; optionType?: string; buttonStyle?: string }) {
  const isControlled = value !== undefined;
  const [inner, setInner] = useState<any>(defaultValue);
  const v = isControlled ? value : inner;
  const setV = (nv: any) => {
    if (!isControlled) setInner(nv);
    onChange?.({ target: { value: nv } });
  };
  const opts = options ?? [];
  return (
    <RadioGroupCtx.Provider value={{ value: v, setValue: setV }}>
      <div className={cx('ds-radio-group', className)} style={{ display: 'inline-flex', gap: 12, ...style }}>
        {opts.length > 0
          ? opts.map((o: any) => {
              const oo = typeof o === 'string' ? { label: o, value: o } : o;
              return <RadioImpl key={String(oo.value)} value={oo.value} disabled={oo.disabled}>{oo.label}</RadioImpl>;
            })
          : children}
      </div>
    </RadioGroupCtx.Provider>
  );
}
export const Radio = Object.assign(RadioImpl, { Group: RadioGroup, Button: RadioImpl });

/* ========================================================================
   Card
   ======================================================================== */
export interface CardProps {
  title?: ReactNode;
  extra?: ReactNode;
  bordered?: boolean;
  size?: 'default' | 'small';
  hoverable?: boolean;
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  headStyle?: CSSProperties;
  children?: ReactNode;
  actions?: ReactNode[];
  cover?: ReactNode;
  styles?: { header?: CSSProperties; body?: CSSProperties };
  onClick?: () => void;
  id?: string;
}
function CardImpl({ title, extra, bordered, size, loading, className, style, bodyStyle, headStyle, children, actions, cover, styles, onClick, id }: CardProps) {
  return (
    <div id={id} className={cx('ds-card', bordered === false && 'ds-card--flat', className)} style={style} onClick={onClick}>
      {cover && <div className="ds-card-cover">{cover}</div>}
      {(title || extra) && (
        <div className="ds-card-head" style={{ ...headStyle, ...(styles?.header || {}) }}>
          {title && <div className="ds-card-title">{title}</div>}
          {extra && <div className="ds-card-extra">{extra}</div>}
        </div>
      )}
      <div className={cx('ds-card-body', size === 'small' && 'ds-card-body--compact')} style={{ ...bodyStyle, ...(styles?.body || {}) }}>
        {loading ? <Spin /> : children}
      </div>
      {actions && actions.length > 0 && (
        <div style={{ display: 'flex', borderTop: '1px solid var(--grey-100)' }}>
          {actions.map((a, i) => <div key={i} style={{ flex: 1, padding: 12, textAlign: 'center' }}>{a}</div>)}
        </div>
      )}
    </div>
  );
}
(CardImpl as any).Meta = function Meta({ title, description, avatar }: { title?: ReactNode; description?: ReactNode; avatar?: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      {avatar}
      <div>
        {title && <div style={{ fontWeight: 600 }}>{title}</div>}
        {description && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{description}</div>}
      </div>
    </div>
  );
};
(CardImpl as any).Grid = function CardGrid({ children, style }: { children?: ReactNode; style?: CSSProperties }) {
  return <div style={{ padding: 16, borderInlineStart: '1px solid var(--grey-100)', ...style }}>{children}</div>;
};

/* ========================================================================
   Tag
   ======================================================================== */
export interface TagProps {
  color?: string;
  icon?: ReactNode;
  closable?: boolean;
  onClose?: (e: any) => void;
  bordered?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  onClick?: () => void;
}
function TagImpl({ color = 'default', icon, closable, onClose, className, style, children, onClick }: TagProps) {
  const known = ['primary', 'success', 'error', 'warning', 'info', 'processing', 'blue', 'geekblue', 'red', 'green', 'orange', 'gold', 'yellow', 'purple', 'pink', 'magenta', 'cyan', 'lime', 'volcano', 'default'];
  const isKnown = known.includes(color);
  const customColor = !isKnown ? color : undefined;
  return (
    <span
      className={cx('ds-tag', isKnown && `ds-tag--${color}`, className)}
      style={customColor ? { background: hexToSoft(customColor), color: customColor, ...style } : style}
      onClick={onClick}
    >
      {icon}
      {children}
      {closable && (
        <button type="button" onClick={onClose} style={{ background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', padding: 0, display: 'inline-flex' }}>
          <CloseOutlined />
        </button>
      )}
    </span>
  );
}
(TagImpl as any).CheckableTag = function CheckableTag({ checked, onChange, children }: { checked?: boolean; onChange?: (v: boolean) => void; children?: ReactNode }) {
  return (
    <span className={cx('ds-tag', checked ? 'ds-tag--primary' : 'ds-tag--default')} style={{ cursor: 'pointer' }} onClick={() => onChange?.(!checked)}>
      {children}
    </span>
  );
};
function hexToSoft(color: string) {
  // Crude helper — for non-standard colors, fade them for the background
  return color.startsWith('#') ? `${color}22` : color;
}

/* ========================================================================
   Alert
   ======================================================================== */
export interface AlertProps {
  type?: 'info' | 'success' | 'warning' | 'error';
  message?: ReactNode;
  description?: ReactNode;
  showIcon?: boolean;
  icon?: ReactNode;
  closable?: boolean;
  onClose?: () => void;
  action?: ReactNode;
  banner?: boolean;
  className?: string;
  style?: CSSProperties;
}
export function Alert({ type = 'info', message, description, showIcon, icon, closable, onClose, action, className, style }: AlertProps) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  const defaultIcon = {
    info: <ExclamationCircleOutlined />,
    success: <CheckCircleOutlined />,
    warning: <ExclamationCircleOutlined />,
    error: <CloseCircleOutlined />,
  }[type];
  return (
    <div className={cx('ds-alert', `ds-alert--${type}`, className)} style={style} role="alert">
      {showIcon && <span className="ds-alert-icon">{icon || defaultIcon}</span>}
      <div className="ds-alert-body">
        {message && <div className="ds-alert-title">{message}</div>}
        {description && <div className="ds-alert-desc">{description}</div>}
      </div>
      {action && <div className="ds-alert-action">{action}</div>}
      {closable && (
        <button className="ds-alert-close" onClick={() => { setClosed(true); onClose?.(); }} aria-label="close">
          <CloseOutlined />
        </button>
      )}
    </div>
  );
}

/* ========================================================================
   Spin / Empty
   ======================================================================== */
export interface SpinProps {
  spinning?: boolean;
  size?: 'small' | 'default' | 'large';
  tip?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  indicator?: ReactNode;
  fullscreen?: boolean;
}
export function Spin({ spinning = true, size, tip, children, className, style }: SpinProps) {
  const dot = (
    <span className={cx('ds-spin', size === 'small' && 'ds-spin-sm', size === 'large' && 'ds-spin-lg', className)} style={style}>
      <span className="ds-spin-dot" />
    </span>
  );
  if (children) {
    return (
      <div className="ds-spin-wrap">
        {children}
        {spinning && <div className="ds-spin-overlay">{dot}{tip && <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>{tip}</div>}</div>}
      </div>
    );
  }
  if (!spinning) return null;
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      {dot}
      {tip && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{tip}</div>}
    </div>
  );
}

export interface EmptyProps {
  description?: ReactNode;
  image?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  imageStyle?: CSSProperties;
}
function EmptyImpl({ description, image, children, className, style }: EmptyProps) {
  return (
    <div className={cx('ds-empty', className)} style={style}>
      <div className="ds-empty-ico">{image ?? '📭'}</div>
      <div className="ds-empty-text">{description ?? 'No data'}</div>
      {children && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}
(EmptyImpl as any).PRESENTED_IMAGE_DEFAULT = null;
(EmptyImpl as any).PRESENTED_IMAGE_SIMPLE = null;

/* ========================================================================
   Typography
   ======================================================================== */
interface TitleProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: 1 | 2 | 3 | 4 | 5;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  children?: ReactNode;
}
function Title({ level = 1, className, style, children, type, ...rest }: TitleProps) {
  const Tag = (`h${level}`) as any;
  const cls = cx('ds-title', `ds-title-${level}`, type && `ds-text--${type}`, className);
  return <Tag className={cls} style={style} {...rest}>{children}</Tag>;
}
interface TextProps extends HTMLAttributes<HTMLSpanElement> {
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  strong?: boolean;
  mark?: boolean;
  code?: boolean;
  copyable?: boolean | { text?: string; tooltips?: [string, string] };
  ellipsis?: boolean;
  children?: ReactNode;
}
function Text({ type, strong, code, ellipsis, className, style, children, ...rest }: TextProps) {
  if (code) return <code className={className} style={style}>{children}</code>;
  return (
    <span
      className={cx('ds-text', type && `ds-text--${type}`, strong && 'ds-text--strong', ellipsis && 'ds-text-ellipsis', className)}
      style={{ ...(ellipsis ? { display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' } : undefined), ...style }}
      {...rest}
    >
      {children}
    </span>
  );
}
function Paragraph({ type, strong, className, style, children, ...rest }: any) {
  return (
    <p className={cx('ds-text', type && `ds-text--${type}`, strong && 'ds-text--strong', className)} style={style} {...rest}>
      {children}
    </p>
  );
}
function Link({ href, target, className, style, children, ...rest }: any) {
  return <a href={href} target={target} className={className} style={style} {...rest}>{children}</a>;
}
export const Typography = Object.assign(
  function TypographyRoot({ children, className, style }: { children?: ReactNode; className?: string; style?: CSSProperties }) {
    return <div className={className} style={style}>{children}</div>;
  },
  { Title, Text, Paragraph, Link },
);

/* ========================================================================
   Layout helpers: Space / Flex / Row / Col / Divider
   ======================================================================== */
export interface SpaceProps {
  size?: number | 'small' | 'middle' | 'large' | [number, number];
  direction?: 'horizontal' | 'vertical';
  align?: 'start' | 'end' | 'center' | 'baseline';
  wrap?: boolean;
  split?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}
function SpaceImpl({ size = 'small', direction = 'horizontal', align, wrap, split, children, className, style }: SpaceProps) {
  const sizePx = typeof size === 'number' ? size : Array.isArray(size) ? size[0] : { small: 8, middle: 16, large: 24 }[size as 'small'];
  const gap2 = Array.isArray(size) ? size[1] : sizePx;
  const arr = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);
  return (
    <div
      className={cx('ds-space', `ds-space-${direction}`, className)}
      style={{ gap: sizePx, rowGap: gap2, alignItems: align, flexWrap: wrap ? 'wrap' : undefined, flexDirection: direction === 'vertical' ? 'column' : 'row', ...style }}
    >
      {split ? arr.map((c, i) => (<Fragment key={i}>{i > 0 && split}{c}</Fragment>)) : children}
    </div>
  );
}
(SpaceImpl as any).Compact = function Compact({ children, style, className }: any) {
  return <div className={cx('ds-space ds-space-horizontal', className)} style={{ gap: 0, ...style }}>{children}</div>;
};

export function Flex({ vertical, align, justify, gap, wrap, children, className, style }: {
  vertical?: boolean; align?: string; justify?: string; gap?: number | string; wrap?: boolean | 'wrap' | 'nowrap'; children?: ReactNode; className?: string; style?: CSSProperties;
}) {
  const gapMap: any = { small: 8, middle: 16, large: 24 };
  return (
    <div
      className={cx('ds-flex', className)}
      style={{
        flexDirection: vertical ? 'column' : 'row',
        alignItems: align, justifyContent: justify,
        gap: typeof gap === 'string' ? gapMap[gap] ?? gap : gap,
        flexWrap: wrap === true ? 'wrap' : wrap === false ? 'nowrap' : (wrap as any),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Row({ gutter, justify, align, wrap = true, children, className, style }: { gutter?: number | [number, number] | number[] | { xs?: number; sm?: number; md?: number }; justify?: string; align?: string; wrap?: boolean; children?: ReactNode; className?: string; style?: CSSProperties }) {
  const [gx, gy] = Array.isArray(gutter) ? gutter : typeof gutter === 'number' ? [gutter, 0] : [0, 0];
  return (
    <div className={cx('ds-row', className)} style={{ margin: `${-gy / 2}px ${-gx / 2}px`, justifyContent: justify as any, alignItems: align as any, flexWrap: wrap ? 'wrap' : 'nowrap', ...style }}>
      {isValidElement(children) || Array.isArray(children) ? (
        Array.isArray(children) ? children.map((c, i) => isValidElement(c) ? cloneElement(c as any, { __gx: gx, __gy: gy, key: (c as any).key ?? i }) : c) : cloneElement(children as any, { __gx: gx, __gy: gy })
      ) : children}
    </div>
  );
}

export interface ColProps {
  span?: number;
  offset?: number;
  xs?: number | { span?: number; offset?: number };
  sm?: number | { span?: number; offset?: number };
  md?: number | { span?: number; offset?: number };
  lg?: number | { span?: number; offset?: number };
  xl?: number | { span?: number; offset?: number };
  xxl?: number | { span?: number; offset?: number };
  flex?: string | number;
  order?: number;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  __gx?: number;
  __gy?: number;
}
export function Col({ span = 24, offset = 0, xs, sm, md, lg, xl, flex, children, className, style, __gx = 0, __gy = 0 }: ColProps) {
  // Crude responsive: prefer md, then sm, then xs, else span
  const w = (() => {
    const pick = (v: any) => (typeof v === 'number' ? v : v?.span);
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1200 && xl !== undefined) return pick(xl);
      if (width >= 992 && lg !== undefined) return pick(lg);
      if (width >= 768 && md !== undefined) return pick(md);
      if (width >= 576 && sm !== undefined) return pick(sm);
      if (xs !== undefined) return pick(xs);
    }
    return span;
  })();
  const styleOut: CSSProperties = flex !== undefined
    ? { flex, padding: `${__gy / 2}px ${__gx / 2}px`, ...style }
    : { flex: `0 0 ${(w / 24) * 100}%`, maxWidth: `${(w / 24) * 100}%`, marginInlineStart: offset ? `${(offset / 24) * 100}%` : undefined, padding: `${__gy / 2}px ${__gx / 2}px`, ...style };
  return <div className={cx('ds-col', className)} style={styleOut}>{children}</div>;
}

export function Divider({ orientation, orientationMargin, plain, style, className, children, type }: any) {
  if (type === 'vertical') return <span className={cx('ds-divider-v', className)} style={style} />;
  if (children) return <div className={cx('ds-divider-label', className)} style={style}>{children}</div>;
  return <hr className={cx('ds-divider', className)} style={style} />;
}

/* ========================================================================
   Layout (app shell)
   ======================================================================== */
function LayoutImpl({ children, hasSider, style, className }: { children?: ReactNode; hasSider?: boolean; style?: CSSProperties; className?: string }) {
  return <div className={cx('ds-layout', className)} style={{ display: 'flex', flexDirection: hasSider ? 'row' : 'column', minHeight: '100vh', ...style }}>{children}</div>;
}
function Sider({ children, width = 256, style, className }: any) {
  return <aside className={cx('ds-sidebar', className)} style={{ width, ...style }}>{children}</aside>;
}
function Header({ children, style, className }: any) {
  return <header className={cx('ds-topbar', className)} style={style}>{children}</header>;
}
function Content({ children, style, className }: any) {
  return <main className={cx('ds-content', className)} style={style}>{children}</main>;
}
function Footer({ children, style, className }: any) {
  return <footer className={className} style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary)', ...style }}>{children}</footer>;
}
(LayoutImpl as any).Sider = Sider;
(LayoutImpl as any).Header = Header;
(LayoutImpl as any).Content = Content;
(LayoutImpl as any).Footer = Footer;

/* ========================================================================
   Menu
   ======================================================================== */
export interface MenuItem {
  key?: string;
  label?: ReactNode;
  icon?: ReactNode;
  children?: MenuItem[];
  type?: 'group' | 'divider';
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
}
export interface MenuProps {
  items?: MenuItem[];
  mode?: 'inline' | 'horizontal' | 'vertical';
  selectedKeys?: string[];
  defaultSelectedKeys?: string[];
  openKeys?: string[];
  onClick?: (info: { key: string; keyPath: string[]; domEvent: any }) => void;
  onSelect?: (info: any) => void;
  style?: CSSProperties;
  className?: string;
  inlineCollapsed?: boolean;
  theme?: 'light' | 'dark';
  children?: ReactNode;
}
export function Menu({ items = [], selectedKeys = [], onClick, style, className }: MenuProps) {
  const renderItem = (it: MenuItem) => {
    if (it.type === 'divider') return <div key={it.key} className="ds-dropdown-divider" />;
    if (it.type === 'group') {
      return (
        <Fragment key={it.key}>
          {it.label && <div className="ds-menu-group-title">{it.label}</div>}
          {it.children?.map(renderItem)}
        </Fragment>
      );
    }
    const key = it.key ?? '';
    const active = selectedKeys.includes(key);
    return (
      <div
        key={key}
        className={cx('ds-menu-item', active && 'active', it.disabled && 'disabled', it.danger && 'danger')}
        onClick={(e) => { if (it.disabled) return; it.onClick?.(); onClick?.({ key, keyPath: [key], domEvent: e }); }}
      >
        {it.icon && <span className="ds-icon">{it.icon}</span>}
        <span>{it.label}</span>
      </div>
    );
  };
  return <nav className={cx('ds-menu', className)} style={style}>{items.map(renderItem)}</nav>;
}

/* ========================================================================
   Dropdown
   ======================================================================== */
export interface DropdownProps {
  menu?: { items?: MenuItem[]; onClick?: (info: { key: string }) => void; selectable?: boolean; selectedKeys?: string[] };
  overlay?: ReactNode;
  placement?: 'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight';
  trigger?: Array<'click' | 'hover'>;
  disabled?: boolean;
  children?: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dropdownRender?: (menu: ReactNode) => ReactNode;
  arrow?: boolean;
}
function DropdownImpl({ menu, overlay, placement = 'bottomLeft', trigger = ['hover'], disabled, children, onOpenChange, dropdownRender }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerClick = trigger.includes('click');
  const triggerHover = trigger.includes('hover');

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) { setOpen(false); onOpenChange?.(false); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onOpenChange]);

  const renderMenu = () => {
    if (overlay) return overlay;
    if (!menu?.items) return null;
    return (
      <>
        {menu.items.map((it, i) => {
          const key = it.key ?? String(i);
          if (it.type === 'divider') return <div key={key} className="ds-dropdown-divider" />;
          return (
            <div
              key={key}
              className={cx('ds-dropdown-item', it.disabled && 'disabled', it.danger && 'danger')}
              onClick={() => { if (it.disabled) return; it.onClick?.(); menu.onClick?.({ key }); setOpen(false); onOpenChange?.(false); }}
            >
              {it.icon && <span className="ds-icon">{it.icon}</span>}
              <span>{it.label}</span>
            </div>
          );
        })}
      </>
    );
  };

  const child = children && isValidElement(children) ? cloneElement(children as any, {
    onClick: (e: any) => {
      (children as any).props.onClick?.(e);
      if (triggerClick) { setOpen((o) => { onOpenChange?.(!o); return !o; }); }
    },
    onMouseEnter: () => triggerHover && setOpen(true),
  }) : children;

  return (
    <div
      ref={wrapRef}
      className="ds-dropdown"
      onMouseLeave={() => triggerHover && !triggerClick && setOpen(false)}
    >
      {child}
      {open && !disabled && (
        <div className={cx('ds-dropdown-menu', `ds-dropdown-${placement}`)}>
          {dropdownRender ? dropdownRender(renderMenu()) : renderMenu()}
        </div>
      )}
    </div>
  );
}
(DropdownImpl as any).Button = function DropdownButton({ menu, children, type, onClick }: any) {
  return (
    <Space>
      <Button type={type} onClick={onClick}>{children}</Button>
      <Dropdown menu={menu} trigger={['click']}>
        <Button type={type}><DownOutlined /></Button>
      </Dropdown>
    </Space>
  );
};

/* ========================================================================
   Modal (component + static methods)
   ======================================================================== */
export interface ModalProps {
  open?: boolean;
  visible?: boolean;
  title?: ReactNode;
  onOk?: () => void;
  onCancel?: () => void;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okButtonProps?: Partial<ButtonProps>;
  cancelButtonProps?: Partial<ButtonProps>;
  confirmLoading?: boolean;
  children?: ReactNode;
  footer?: ReactNode | null;
  width?: number | string;
  centered?: boolean;
  maskClosable?: boolean;
  closable?: boolean;
  destroyOnClose?: boolean;
  className?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  zIndex?: number;
  afterClose?: () => void;
  styles?: { body?: CSSProperties; header?: CSSProperties };
}
function ModalImpl({ open, visible, title, onOk, onCancel, okText = 'OK', cancelText = 'Cancel', okButtonProps, cancelButtonProps, confirmLoading, children, footer, width = 520, maskClosable = true, closable = true, className, style, bodyStyle, styles }: ModalProps) {
  const isOpen = open ?? visible ?? false;
  if (!isOpen) return null;
  return createPortal(
    <div className="ds-modal-mask" onMouseDown={(e) => { if (e.target === e.currentTarget && maskClosable) onCancel?.(); }}>
      <div className={cx('ds-modal', className)} style={{ width, ...style }} onClick={(e) => e.stopPropagation()}>
        {(title || closable) && (
          <div className="ds-modal-header" style={styles?.header}>
            <div className="ds-modal-title">{title}</div>
            {closable && <button className="ds-modal-close" onClick={onCancel} aria-label="close"><CloseOutlined /></button>}
          </div>
        )}
        <div className="ds-modal-body" style={{ ...bodyStyle, ...(styles?.body || {}) }}>{children}</div>
        {footer !== null && (
          footer ? <div className="ds-modal-footer">{footer}</div> : (
            <div className="ds-modal-footer">
              <Button onClick={onCancel} {...cancelButtonProps}>{cancelText}</Button>
              <Button type="primary" loading={confirmLoading} onClick={onOk} {...okButtonProps}>{okText}</Button>
            </div>
          )
        )}
      </div>
    </div>,
    document.body,
  );
}

// Static Modal.confirm / info / success / error / warning
type ConfirmArgs = Omit<ModalProps, 'open' | 'visible'> & { content?: ReactNode; icon?: ReactNode };
function openConfirm(kind: 'confirm' | 'info' | 'success' | 'error' | 'warning', args: ConfirmArgs) {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const cleanup = () => {
    try { (container as any)._cleanup?.(); } catch {}
    setTimeout(() => { try { document.body.removeChild(container); } catch {} }, 0);
  };
  const Wrapper = () => {
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const close = () => { setOpen(false); args.afterClose?.(); cleanup(); };
    const handleOk = async () => {
      try {
        setLoading(true);
        const r: any = args.onOk?.();
        if (r && typeof r.then === 'function') await r;
        close();
      } catch { setLoading(false); }
    };
    const handleCancel = () => { args.onCancel?.(); close(); };
    return (
      <Modal
        open={open}
        title={args.title}
        okText={args.okText}
        cancelText={args.cancelText}
        okButtonProps={kind === 'confirm' ? { danger: true, ...(args.okButtonProps || {}) } : args.okButtonProps}
        confirmLoading={loading}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={kind === 'confirm' ? undefined : <Button type="primary" onClick={close}>{args.okText ?? 'OK'}</Button>}
        width={args.width}
      >
        {args.content}
      </Modal>
    );
  };
  import('react-dom/client').then(({ createRoot }) => {
    const root = createRoot(container);
    (container as any)._cleanup = () => root.unmount();
    root.render(<Wrapper />);
  });
  return { destroy: cleanup, update: () => {} };
}
(ModalImpl as any).confirm = (args: ConfirmArgs) => openConfirm('confirm', args);
(ModalImpl as any).info = (args: ConfirmArgs) => openConfirm('info', args);
(ModalImpl as any).success = (args: ConfirmArgs) => openConfirm('success', args);
(ModalImpl as any).error = (args: ConfirmArgs) => openConfirm('error', args);
(ModalImpl as any).warning = (args: ConfirmArgs) => openConfirm('warning', args);
(ModalImpl as any).destroyAll = () => {};

/* ========================================================================
   Drawer
   ======================================================================== */
export interface DrawerProps {
  open?: boolean;
  visible?: boolean;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  title?: ReactNode;
  onClose?: () => void;
  width?: number | string;
  height?: number | string;
  children?: ReactNode;
  footer?: ReactNode;
  extra?: ReactNode;
  closable?: boolean;
  maskClosable?: boolean;
  className?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  destroyOnClose?: boolean;
  forceRender?: boolean;
  zIndex?: number;
  styles?: { body?: CSSProperties; header?: CSSProperties };
}
export function Drawer({ open, visible, placement = 'right', title, onClose, width = 480, height, children, footer, extra, closable = true, maskClosable = true, className, style, bodyStyle, styles }: DrawerProps) {
  const isOpen = open ?? visible ?? false;
  if (!isOpen) return null;
  const drawerStyle: CSSProperties = placement === 'top' || placement === 'bottom'
    ? { height, ...style }
    : { width, ...style };
  return createPortal(
    <>
      <div className="ds-drawer-mask" onClick={() => maskClosable && onClose?.()} />
      <div className={cx('ds-drawer', `ds-drawer-${placement}`, className)} style={drawerStyle}>
        {(title || closable || extra) && (
          <div className="ds-drawer-header" style={styles?.header}>
            <div className="ds-drawer-title">{title}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {extra}
              {closable && <button className="ds-modal-close" onClick={onClose} aria-label="close"><CloseOutlined /></button>}
            </div>
          </div>
        )}
        <div className="ds-drawer-body" style={{ ...bodyStyle, ...(styles?.body || {}) }}>{children}</div>
        {footer && <div className="ds-drawer-footer">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}

/* ========================================================================
   Avatar
   ======================================================================== */
export interface AvatarProps {
  size?: number | 'small' | 'default' | 'large';
  src?: string;
  icon?: ReactNode;
  alt?: string;
  shape?: 'circle' | 'square';
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}
function AvatarImpl({ size = 'default', src, icon, alt, shape = 'circle', children, style, className }: AvatarProps) {
  const sz = typeof size === 'number' ? size : { small: 24, default: 32, large: 48 }[size];
  return (
    <span className={cx('ds-avatar', shape === 'square' && 'ds-avatar-square', className)}
      style={{ width: sz, height: sz, borderRadius: shape === 'square' ? 'var(--r-md)' : '50%', fontSize: sz! / 2.2, ...style }}>
      {src ? <img src={src} alt={alt} /> : (icon ?? children)}
    </span>
  );
}
(AvatarImpl as any).Group = function Group({ children, maxCount, size, style, className }: any) {
  return <div className={cx('ds-avatar-group', className)} style={{ display: 'inline-flex', ...style }}>{children}</div>;
};

/* ========================================================================
   Descriptions
   ======================================================================== */
export interface DescriptionsProps {
  title?: ReactNode;
  column?: number | { xs?: number; sm?: number; md?: number };
  bordered?: boolean;
  size?: 'default' | 'small' | 'middle';
  layout?: 'horizontal' | 'vertical';
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  extra?: ReactNode;
  colon?: boolean;
  labelStyle?: CSSProperties;
  contentStyle?: CSSProperties;
}
function DescriptionsImpl({ title, column = 1, children, className, style, extra }: DescriptionsProps) {
  const col = typeof column === 'number' ? column : 1;
  return (
    <div className={cx('ds-descriptions', className)} style={style}>
      {(title || extra) && (
        <div className="ds-descriptions-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{title}</span>
          {extra}
        </div>
      )}
      <div className={`ds-descriptions-body ${col > 1 ? `cols-${col}` : ''}`}>
        {children}
      </div>
    </div>
  );
}
(DescriptionsImpl as any).Item = function DescriptionsItem({ label, children, span, labelStyle, contentStyle }: { label?: ReactNode; children?: ReactNode; span?: number; labelStyle?: CSSProperties; contentStyle?: CSSProperties }) {
  return (
    <div className="ds-descriptions-item" style={{ gridColumn: span ? `span ${span}` : undefined }}>
      <div className="ds-descriptions-label" style={labelStyle}>{label}</div>
      <div className="ds-descriptions-value" style={contentStyle}>{children}</div>
    </div>
  );
};

/* ========================================================================
   Image
   ======================================================================== */
export interface ImageProps {
  src?: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  preview?: boolean | { mask?: ReactNode; visible?: boolean; onVisibleChange?: (visible: boolean) => void };
  fallback?: string;
  placeholder?: ReactNode;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
}
function ImageImpl({ src, alt, width, height, style, className, onClick }: ImageProps) {
  return (
    <span className={cx('ds-image', className)} style={{ width, height }} onClick={onClick}>
      <img src={src} alt={alt} style={{ width, height, borderRadius: 'var(--r-md)', objectFit: 'cover', ...style }} />
    </span>
  );
}
(ImageImpl as any).PreviewGroup = function PreviewGroup({ children }: { children?: ReactNode }) {
  return <>{children}</>;
};

/* ========================================================================
   Table
   ======================================================================== */
export interface TableColumn<T = any> {
  key?: string;
  dataIndex?: string | string[];
  title?: ReactNode;
  render?: (value: any, record: T, index: number) => ReactNode;
  sorter?: boolean | ((a: T, b: T) => number);
  sortOrder?: 'ascend' | 'descend' | null;
  width?: number | string;
  align?: 'left' | 'right' | 'center' | 'start' | 'end';
  fixed?: 'left' | 'right';
  ellipsis?: boolean;
  className?: string;
  colSpan?: number;
  onCell?: (record: T) => { colSpan?: number; rowSpan?: number };
  filters?: Array<{ text: ReactNode; value: any }>;
  onFilter?: (value: any, record: T) => boolean;
  filteredValue?: any[];
  filterDropdown?: any;
  children?: TableColumn<T>[];
}
export interface TableProps<T = any> {
  columns?: TableColumn<T>[];
  dataSource?: T[];
  rowKey?: string | ((r: T) => string);
  loading?: boolean | { spinning?: boolean; tip?: ReactNode };
  pagination?: false | {
    current?: number;
    pageSize?: number;
    total?: number;
    showSizeChanger?: boolean;
    showTotal?: (total: number, range: [number, number]) => ReactNode;
    onChange?: (page: number, pageSize: number) => void;
    pageSizeOptions?: string[];
    onShowSizeChange?: (current: number, size: number) => void;
    hideOnSinglePage?: boolean;
    position?: string[];
  };
  onChange?: (pagination: any, filters: any, sorter: any, extra?: any) => void;
  rowClassName?: (r: T, i: number) => string;
  rowSelection?: any;
  scroll?: { x?: number | string; y?: number | string };
  size?: 'small' | 'middle' | 'large';
  locale?: { emptyText?: ReactNode };
  bordered?: boolean;
  className?: string;
  style?: CSSProperties;
  sticky?: boolean;
  expandable?: any;
  summary?: any;
  title?: () => ReactNode;
  footer?: () => ReactNode;
  onRow?: (record: T, index: number) => any;
  showHeader?: boolean;
  tableLayout?: 'auto' | 'fixed';
  sortDirections?: Array<'ascend' | 'descend' | null>;
  showSorterTooltip?: boolean;
}
export function Table<T = any>(props: TableProps<T>) {
  const { columns = [], dataSource = [], rowKey = 'key', loading, pagination, onChange, rowClassName, scroll, size, locale, className, style, onRow, showHeader = true, rowSelection } = props;
  const isLoading = typeof loading === 'object' ? !!loading.spinning : !!loading;

  const keyOf = (r: any, i: number) => (typeof rowKey === 'function' ? rowKey(r) : r?.[rowKey]) ?? i;

  const handleHeaderClick = (col: TableColumn<T>) => {
    if (!col.sorter) return;
    const field = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex;
    const current = col.sortOrder;
    const next = current === 'ascend' ? 'descend' : current === 'descend' ? null : 'ascend';
    onChange?.(pagination, {}, { field, columnKey: col.key ?? field, order: next, column: col });
  };

  const renderCellValue = (col: TableColumn<T>, record: any, index: number) => {
    const value = Array.isArray(col.dataIndex) ? col.dataIndex.reduce((acc: any, k) => acc?.[k], record) : col.dataIndex ? record?.[col.dataIndex] : undefined;
    return col.render ? col.render(value, record, index) : value;
  };

  const selectedKeys: (string | number)[] = rowSelection?.selectedRowKeys ?? [];
  const allKeys = dataSource.map((r: any, i) => String(keyOf(r, i)));
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.includes(k));
  const someSelected = !allSelected && allKeys.some((k) => selectedKeys.includes(k));

  const toggleAll = () => {
    if (!rowSelection?.onChange) return;
    rowSelection.onChange(allSelected ? [] : allKeys, allSelected ? [] : dataSource);
  };

  const toggleRow = (key: string, record: any) => {
    if (!rowSelection?.onChange) return;
    const next = selectedKeys.includes(key)
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];
    rowSelection.onChange(next, dataSource.filter((_: any, i: number) => next.includes(String(keyOf(_, i)))));
  };

  const totalCols = columns.length + (rowSelection ? 1 : 0);
  const tableScrollStyle: CSSProperties | undefined = scroll?.x
    ? {
        minWidth: typeof scroll.x === 'number' ? scroll.x : scroll.x,
        width: typeof scroll.x === 'number' ? scroll.x : scroll.x,
      }
    : undefined;

  const body = (
    <div className="ds-table-wrap" style={style}>
      <div style={{ overflowX: scroll?.x ? 'auto' : 'visible', maxHeight: scroll?.y, WebkitOverflowScrolling: 'touch' }}>
        <table className={cx('ds-table', size === 'small' && 'ds-table--sm', className)} style={tableScrollStyle}>
          {showHeader && (
            <thead>
              <tr>
                {rowSelection && (
                  <th style={{ width: 40, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                    />
                  </th>
                )}
                {columns.map((col, i) => (
                  <th
                    key={col.key ?? (col.dataIndex as string) ?? i}
                    className={cx(col.sorter && 'sortable', col.ellipsis && 'ellipsis', col.className)}
                    style={{ width: col.width, textAlign: col.align as any }}
                    onClick={() => handleHeaderClick(col)}
                  >
                    {col.title}
                    {col.sorter && (
                      <span style={{ marginInlineStart: 4, fontSize: 10, color: col.sortOrder ? 'var(--color-primary-dark)' : 'var(--text-tertiary)' }}>
                        {col.sortOrder === 'ascend' ? '▲' : col.sortOrder === 'descend' ? '▼' : '↕'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {dataSource.length === 0 ? (
              <tr>
                <td colSpan={totalCols} style={{ padding: 0 }}>
                  <Empty description={locale?.emptyText ?? 'No data'} />
                </td>
              </tr>
            ) : (
              dataSource.map((record: any, index) => {
                const rowProps = onRow?.(record, index) || {};
                const rowKeyStr = String(keyOf(record, index));
                const isChecked = selectedKeys.includes(rowKeyStr);
                return (
                  <tr
                    key={rowKeyStr}
                    className={cx(rowClassName?.(record, index), isChecked && 'ds-table-row--selected')}
                    {...rowProps}
                  >
                    {rowSelection && (
                      <td style={{ width: 40, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleRow(rowKeyStr, record)}
                        />
                      </td>
                    )}
                    {columns.map((col, i) => (
                      <td
                        key={col.key ?? (col.dataIndex as string) ?? i}
                        className={cx(col.ellipsis && 'ellipsis', col.className)}
                        style={{ textAlign: col.align as any }}
                      >
                        {renderCellValue(col, record, index)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {pagination !== false && pagination && (
        <TablePagination pagination={pagination} onChange={(p: number, s: number) => {
          pagination.onChange?.(p, s);
          onChange?.({ ...pagination, current: p, pageSize: s }, {}, {});
        }} />
      )}
    </div>
  );
  return <Spin spinning={isLoading}>{body}</Spin>;
}

function TablePagination({ pagination, onChange }: any) {
  const current = pagination.current ?? 1;
  const pageSize = pagination.pageSize ?? 10;
  const total = pagination.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);
  if (pagination.hideOnSinglePage && pages <= 1) return null;
  const go = (p: number) => { if (p >= 1 && p <= pages) onChange(p, pageSize); };
  const nums: number[] = [];
  const max = 5;
  let from = Math.max(1, current - 2);
  let to = Math.min(pages, from + max - 1);
  from = Math.max(1, to - max + 1);
  for (let i = from; i <= to; i++) nums.push(i);
  return (
    <div className="ds-pagination">
      <div>
        {pagination.showTotal
          ? pagination.showTotal(total, [start, end])
          : `${start}-${end} of ${total}`}
      </div>
      <div className="ds-pagination-pages">
        <button className="ds-page-btn" disabled={current <= 1} onClick={() => go(current - 1)}><LeftOutlined /></button>
        {from > 1 && (<><button className="ds-page-btn" onClick={() => go(1)}>1</button>{from > 2 && <span style={{ padding: '0 4px' }}>…</span>}</>)}
        {nums.map((n) => <button key={n} className={cx('ds-page-btn', n === current && 'active')} onClick={() => go(n)}>{n}</button>)}
        {to < pages && (<>{to < pages - 1 && <span style={{ padding: '0 4px' }}>…</span>}<button className="ds-page-btn" onClick={() => go(pages)}>{pages}</button></>)}
        <button className="ds-page-btn" disabled={current >= pages} onClick={() => go(current + 1)}><RightOutlined /></button>
      </div>
    </div>
  );
}

/* ========================================================================
   DatePicker (minimal)
   ======================================================================== */
export interface DatePickerProps {
  value?: any;
  defaultValue?: any;
  onChange?: (value: any, dateString: string) => void;
  placeholder?: string;
  format?: string;
  showTime?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: 'small' | 'middle' | 'large';
  status?: 'error' | 'warning';
  disabledDate?: (d: any) => boolean;
  picker?: 'date' | 'week' | 'month' | 'year';
}
function toDateStr(v: any, showTime = false): string {
  if (!v) return '';
  if (typeof v === 'string') {
    const match = showTime
      ? v.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/)
      : v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return showTime ? `${match[1]}T${match[2]}` : match[1];
  }
  if (typeof v?.format === 'function') {
    return v.format(showTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD');
  }
  const d = v instanceof Date ? v : (v?.toDate ? v.toDate() : new Date(v));
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  if (!showTime) return `${year}-${month}-${day}`;
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
function DatePickerImpl({ value, defaultValue, onChange, placeholder, showTime, disabled, allowClear, className, style, status }: DatePickerProps) {
  const isControlled = value !== undefined;
  const [inner, setInner] = useState<string>(toDateStr(defaultValue, showTime));
  const shown = isControlled ? toDateStr(value, showTime) : inner;
  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!isControlled) setInner(v);
    if (v) {
      const d = new Date(v);
      onChange?.(d as any, v);
    } else {
      onChange?.(null as any, '');
    }
  };
  return (
    <input
      type={showTime ? 'datetime-local' : 'date'}
      className={cx('ds-input', status === 'error' && 'ds-input-error', className)}
      style={style}
      value={shown}
      onChange={handle}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}
function RangePicker({ value, onChange, showTime, disabled, className, style, placeholder }: any) {
  const [start, end] = value || [null, null];
  return (
    <div className="ds-flex" style={{ gap: 6, alignItems: 'center', ...style }}>
      <DatePicker
        value={start}
        showTime={showTime}
        disabled={disabled}
        className={className}
        placeholder={placeholder?.[0]}
        onChange={(d) => onChange?.([d, end], ['', ''])}
      />
      <span style={{ color: 'var(--text-tertiary)' }}>→</span>
      <DatePicker
        value={end}
        showTime={showTime}
        disabled={disabled}
        className={className}
        placeholder={placeholder?.[1]}
        onChange={(d) => onChange?.([start, d], ['', ''])}
      />
    </div>
  );
}
(DatePickerImpl as any).RangePicker = RangePicker;

/* ========================================================================
   Form (compatible with antd Form API — minimal but functional)
   ======================================================================== */
type Rule = { required?: boolean; message?: ReactNode; type?: string; min?: number; max?: number; pattern?: RegExp; whitespace?: boolean; validator?: (_r: any, value: any) => Promise<any> | void };

type FormInstance<T = any> = {
  getFieldValue: (name: string) => any;
  getFieldsValue: (names?: string[]) => T;
  setFieldValue: (name: string, value: any) => void;
  setFieldsValue: (values: Partial<T>) => void;
  resetFields: (names?: string[]) => void;
  validateFields: (names?: string[]) => Promise<T>;
  submit: () => void;
  isFieldTouched: (name: string) => boolean;
};

interface FormCtx {
  values: Record<string, any>;
  setValue: (name: string, val: any) => void;
  errors: Record<string, string | undefined>;
  setError: (name: string, err?: string) => void;
  register: (name: string, rules?: Rule[], initialValue?: any) => void;
  unregister: (name: string) => void;
  onFinish?: (vals: any) => void;
  layout?: 'horizontal' | 'vertical' | 'inline';
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  colon?: boolean;
  labelCol?: any;
  wrapperCol?: any;
  requiredMark?: boolean;
}
const FormContext = createContext<FormCtx | null>(null);

export function useForm<T = any>(): [FormInstance<T>] {
  const ref = useRef<any>({
    values: {},
    errors: {},
    rules: {} as Record<string, Rule[]>,
    touched: {} as Record<string, boolean>,
    setCtxValue: undefined as any,
    setCtxError: undefined as any,
    getCtxValues: () => ref.current.values,
  });
  const instance: FormInstance<T> = useMemo(
    () => ({
      getFieldValue: (name) => ref.current.values[name],
      getFieldsValue: (names) => {
        const v = { ...ref.current.values };
        if (!names) return v;
        const o: any = {};
        names.forEach((n) => (o[n] = v[n]));
        return o;
      },
      setFieldValue: (name, value) => {
        ref.current.values[name] = value;
        ref.current.setCtxValue?.(name, value);
      },
      setFieldsValue: (values) => {
        Object.entries(values as any).forEach(([k, v]) => {
          ref.current.values[k] = v;
          ref.current.setCtxValue?.(k, v);
        });
      },
      resetFields: (names) => {
        if (names) names.forEach((n) => { delete ref.current.values[n]; ref.current.setCtxValue?.(n, undefined); });
        else {
          Object.keys(ref.current.values).forEach((n) => { ref.current.values[n] = undefined; ref.current.setCtxValue?.(n, undefined); });
        }
      },
      validateFields: async () => {
        const errors: Record<string, string> = {};
        for (const [name, rules] of Object.entries(ref.current.rules as Record<string, Rule[]>)) {
          const value = ref.current.values[name];
          for (const r of rules) {
            if (r.required && (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0))) {
              errors[name] = (r.message as string) || 'Required';
              break;
            }
            if (r.type === 'email' && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
              errors[name] = (r.message as string) || 'Invalid email';
              break;
            }
            if (r.min !== undefined && typeof value === 'string' && value.length < r.min) { errors[name] = (r.message as string) || `Min ${r.min}`; break; }
            if (r.max !== undefined && typeof value === 'string' && value.length > r.max) { errors[name] = (r.message as string) || `Max ${r.max}`; break; }
            if (r.pattern && value && !r.pattern.test(value)) { errors[name] = (r.message as string) || 'Invalid'; break; }
            if (r.validator) {
              try { await r.validator({}, value); } catch (e: any) { errors[name] = e?.message || (r.message as string) || 'Invalid'; break; }
            }
          }
        }
        // Push errors to UI
        Object.keys(ref.current.rules).forEach((n) => ref.current.setCtxError?.(n, errors[n]));
        if (Object.keys(errors).length) throw { errorFields: Object.entries(errors).map(([name, error]) => ({ name: [name], errors: [error] })), values: ref.current.values };
        return ref.current.values as T;
      },
      submit: () => {
        (instance.validateFields() as any).then((vals: T) => ref.current.onFinish?.(vals)).catch(() => {});
      },
      isFieldTouched: (name) => !!ref.current.touched[name],
    }),
    [],
  );
  (instance as any)._ref = ref;
  return [instance];
}

export interface FormProps<T = any> {
  form?: FormInstance<T>;
  layout?: 'horizontal' | 'vertical' | 'inline';
  onFinish?: (values: T) => void;
  onFinishFailed?: (e: any) => void;
  initialValues?: Partial<T>;
  autoComplete?: string;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: 'small' | 'middle' | 'large';
  colon?: boolean;
  labelCol?: any;
  wrapperCol?: any;
  requiredMark?: boolean;
  onValuesChange?: (changed: any, all: any) => void;
  name?: string;
  id?: string;
  component?: any;
  preserve?: boolean;
  validateTrigger?: string | string[];
  scrollToFirstError?: boolean;
}
function FormImpl<T = any>({ form: formProp, layout = 'horizontal', onFinish, initialValues, children, disabled, className, style, colon, labelCol, wrapperCol, requiredMark, onValuesChange, size, autoComplete, name, id }: FormProps<T>) {
  const [internalForm] = useForm<T>();
  const form = (formProp || internalForm) as FormInstance<T> & { _ref: any };
  const [values, setValues] = useState<Record<string, any>>(initialValues as any || {});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const rulesRef = useRef<Record<string, Rule[]>>({});

  useEffect(() => {
    // sync imperative ref
    (form as any)._ref.current.values = values;
    (form as any)._ref.current.errors = errors;
    (form as any)._ref.current.rules = rulesRef.current;
    (form as any)._ref.current.onFinish = onFinish;
    (form as any)._ref.current.setCtxValue = (n: string, v: any) => setValues((prev) => ({ ...prev, [n]: v }));
    (form as any)._ref.current.setCtxError = (n: string, e?: string) => setErrors((prev) => ({ ...prev, [n]: e }));
  });

  const ctx: FormCtx = {
    values,
    setValue: (n, v) => {
      setValues((prev) => {
        const next = { ...prev, [n]: v };
        onValuesChange?.({ [n]: v }, next);
        return next;
      });
      (form as any)._ref.current.touched[n] = true;
    },
    errors,
    setError: (n, e) => setErrors((prev) => ({ ...prev, [n]: e })),
    register: (n, rules, initialValue) => {
      if (rules) rulesRef.current[n] = rules;
      if (initialValue !== undefined && values[n] === undefined) {
        setValues((prev) => (prev[n] === undefined ? { ...prev, [n]: initialValue } : prev));
      }
    },
    unregister: (n) => { delete rulesRef.current[n]; },
    onFinish: onFinish as any,
    layout, disabled, size, colon, labelCol, wrapperCol, requiredMark,
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.submit();
  };

  return (
    <FormContext.Provider value={ctx}>
      <form
        id={id}
        name={name}
        className={cx('ds-form', layout === 'horizontal' && 'ds-form--horizontal', className)}
        style={style}
        autoComplete={autoComplete}
        onSubmit={handleSubmit}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}

export interface FormItemProps {
  name?: string;
  label?: ReactNode;
  rules?: Rule[];
  valuePropName?: string;
  trigger?: string;
  initialValue?: any;
  required?: boolean;
  help?: ReactNode;
  extra?: ReactNode;
  noStyle?: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  hidden?: boolean;
  labelCol?: any;
  wrapperCol?: any;
  validateStatus?: 'success' | 'warning' | 'error' | 'validating';
  tooltip?: ReactNode;
  hasFeedback?: boolean;
  shouldUpdate?: boolean | ((prev: any, next: any) => boolean);
  dependencies?: string[];
  normalize?: (value: any, prev: any, all: any) => any;
  getValueFromEvent?: (...args: any[]) => any;
  getValueProps?: (value: any) => any;
  preserve?: boolean;
}

function FormItem({ name, label, rules, valuePropName = 'value', trigger = 'onChange', initialValue, required, help, children, className, style, hidden, noStyle, validateStatus, getValueFromEvent, getValueProps }: FormItemProps) {
  const ctx = useContext(FormContext);
  if (hidden) return null;

  useEffect(() => {
    if (!ctx || !name) return;
    ctx.register(name, rules, initialValue);
    return () => ctx.unregister(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const isRequired = required || rules?.some((r) => r.required);
  const error = ctx?.errors[name as string];
  const status = error ? 'error' : validateStatus;

  const onValueChange = useCallback((val: any) => {
    if (!ctx || !name) return;
    ctx.setValue(name, val);
    if (error) ctx.setError(name, undefined);
  }, [ctx, name, error]);

  // Inject value/onChange to child (antd pattern)
  const injectChild = (child: ReactElement) => {
    const childProps: any = child.props;
    const val = ctx && name ? ctx.values[name] : childProps[valuePropName];
    const originalHandler = childProps[trigger];
    const handleChange = (...args: any[]) => {
      let next: any;
      if (getValueFromEvent) next = getValueFromEvent(...args);
      else {
        const v = args[0];
        if (v && typeof v === 'object' && 'target' in v) next = v.target.type === 'checkbox' ? v.target.checked : v.target.value;
        else next = v;
      }
      onValueChange(next);
      originalHandler?.(...args);
    };
    const propsToInject: any = { [valuePropName]: val ?? '', [trigger]: handleChange };
    if (getValueProps) Object.assign(propsToInject, getValueProps(val));
    if (status === 'error' && !childProps.status) propsToInject.status = 'error';
    return cloneElement(child, propsToInject);
  };

  let rendered: ReactNode = children;
  if (typeof children === 'function') {
    rendered = (children as any)(ctx);
  } else if (isValidElement(children) && name) {
    rendered = injectChild(children);
  }

  if (noStyle) return <>{rendered}</>;

  return (
    <div className={cx('ds-form-item', className)} style={style}>
      {label && (
        <label className={cx('ds-form-label', isRequired && 'required')}>{label}</label>
      )}
      {rendered}
      {(error || help) && <div className={error ? 'ds-form-error' : 'ds-form-hint'}>{error || help}</div>}
    </div>
  );
}

function FormList({ name, children }: { name: string; children: (fields: Array<{ key: number; name: number }>, ops: { add: (v?: any) => void; remove: (i: number) => void; move: (a: number, b: number) => void }) => ReactNode }) {
  const ctx = useContext(FormContext);
  const list: any[] = (ctx?.values[name] as any[]) || [];
  const ops = {
    add: (v?: any) => ctx?.setValue(name, [...list, v]),
    remove: (i: number) => ctx?.setValue(name, list.filter((_, idx) => idx !== i)),
    move: (a: number, b: number) => { const c = [...list]; const [x] = c.splice(a, 1); c.splice(b, 0, x); ctx?.setValue(name, c); },
  };
  const fields = list.map((_, i) => ({ key: i, name: i }));
  return <>{children(fields, ops)}</>;
}

function formUseWatch(name: string, form?: FormInstance) {
  const ctx = useContext(FormContext);
  if (ctx && name) return ctx.values[name];
  return form?.getFieldValue(name);
}

export const Form = Object.assign(FormImpl, {
  Item: FormItem,
  List: FormList,
  useForm,
  useWatch: formUseWatch,
  Provider: ({ children }: { children?: ReactNode }) => <>{children}</>,
  ErrorList: () => null as null,
});

/* ========================================================================
   App (message / notification / modal) — antd-compatible
   ======================================================================== */
const AppApi = {
  message: {
    success: (m: ReactNode) => toast.success(m as any),
    error: (m: ReactNode) => toast.error(m as any),
    warning: (m: ReactNode) => toast(m as any, { icon: '⚠️' }),
    info: (m: ReactNode) => toast(m as any),
    loading: (m: ReactNode) => toast.loading(m as any),
    open: (opts: any) => toast(opts?.content ?? ''),
    destroy: () => toast.dismiss(),
  },
  notification: {
    success: (opts: any) => toast.success(opts?.message ?? opts?.description ?? ''),
    error: (opts: any) => toast.error(opts?.message ?? opts?.description ?? ''),
    warning: (opts: any) => toast(opts?.message ?? opts?.description ?? '', { icon: '⚠️' }),
    info: (opts: any) => toast(opts?.message ?? opts?.description ?? ''),
    open: (opts: any) => toast(opts?.message ?? opts?.description ?? ''),
    destroy: () => toast.dismiss(),
  },
  modal: { confirm: (args: any) => (ModalImpl as any).confirm(args) },
};
function AppImpl({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}
export const App = Object.assign(AppImpl, { useApp: () => AppApi });

// Top-level message/notification proxies for `import { message } from 'antd'`
export const message = AppApi.message;
export const notification = AppApi.notification;

/* ========================================================================
   ConfigProvider (no-op passthrough)
   ======================================================================== */
export function ConfigProvider({ children }: any) {
  return <>{children}</>;
}
(ConfigProvider as any).config = () => {};

/* ========================================================================
   Theme (no-op)
   ======================================================================== */
export const theme = {
  defaultAlgorithm: {},
  darkAlgorithm: {},
  compactAlgorithm: {},
  useToken: () => ({ token: {}, hashId: '' }),
};

/* ========================================================================
   Misc / placeholders often imported from antd
   ======================================================================== */
export function Statistic({ title, value, prefix, suffix, valueStyle, precision, className, style }: { title?: ReactNode; value?: any; prefix?: ReactNode; suffix?: ReactNode; valueStyle?: CSSProperties; precision?: number; className?: string; style?: CSSProperties }) {
  const display = typeof value === 'number' && precision !== undefined ? value.toFixed(precision) : value;
  return (
    <div className={cx('ds-stat', className)} style={style}>
      {title && <div className="ds-stat-head">{title}</div>}
      <div className="ds-stat-value" style={valueStyle}>
        {prefix && <span className="ds-stat-prefix">{prefix}</span>}
        {display}
        {suffix && <span style={{ marginInlineStart: 6, color: 'var(--text-secondary)' }}>{suffix}</span>}
      </div>
    </div>
  );
}

export function Progress({ percent = 0, status, size, showInfo = true, className, style, strokeColor, type }: any) {
  const color = status === 'success' ? 'var(--success)' : status === 'exception' ? 'var(--error)' : strokeColor || 'var(--color-primary)';
  return (
    <div className={cx('ds-progress', className)} style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}>
      <div style={{ flex: 1, height: size === 'small' ? 6 : 8, background: 'var(--grey-100)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, percent))}%`, height: '100%', background: color, transition: 'width .3s' }} />
      </div>
      {showInfo && <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 36, textAlign: 'end' }}>{Math.round(percent)}%</span>}
    </div>
  );
}

function BadgeImpl({ count, showZero, overflowCount = 99, dot, status, text, color, offset, children, className, style }: any) {
  const visible = dot || (count !== undefined && (count > 0 || showZero));
  const display = count > overflowCount ? `${overflowCount}+` : count;
  if (status) {
    const colorMap: any = { success: 'var(--success)', error: 'var(--error)', warning: 'var(--warning)', processing: 'var(--info)', default: 'var(--grey-400)' };
    return <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...style }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: color || colorMap[status] }} />{text}</span>;
  }
  return (
    <span className={className} style={{ position: 'relative', display: 'inline-block', ...style }}>
      {children}
      {visible && (
        <span style={{ position: 'absolute', top: -6, insetInlineEnd: -6, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: color || 'var(--error)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {dot ? null : display}
        </span>
      )}
    </span>
  );
}
(BadgeImpl as any).Ribbon = function Ribbon({ text, color, children }: any) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <span style={{ position: 'absolute', top: 8, insetInlineEnd: -4, background: color || 'var(--color-primary)', color: '#fff', padding: '2px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4 }}>{text}</span>
    </div>
  );
};

export function Skeleton({ active = true, loading = true, children, paragraph = true, title = true, avatar, className, style }: any) {
  if (!loading) return <>{children}</>;
  return (
    <div className={cx('ds-skeleton', active && 'ds-skel-active', className)} style={style}>
      <div style={{ display: 'flex', gap: 12 }}>
        {avatar && <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--grey-100)' }} />}
        <div style={{ flex: 1 }}>
          {title !== false && <div style={{ height: 14, background: 'var(--grey-100)', borderRadius: 4, width: '40%', marginBottom: 10 }} />}
          {paragraph !== false && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={{ height: 10, background: 'var(--grey-100)', borderRadius: 4, width: i === 2 ? '75%' : '100%', marginBottom: 8 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TabsImpl({ items = [], activeKey, defaultActiveKey, onChange, type, tabPosition, className, style }: { items?: Array<{ key: string; label: ReactNode; children?: ReactNode; disabled?: boolean }>; activeKey?: string; defaultActiveKey?: string; onChange?: (k: string) => void; type?: string; tabPosition?: string; className?: string; style?: CSSProperties }) {
  const isControlled = activeKey !== undefined;
  const [inner, setInner] = useState(defaultActiveKey || items[0]?.key);
  const key = isControlled ? activeKey : inner;
  const set = (k: string) => { if (!isControlled) setInner(k); onChange?.(k); };
  const current = items.find((i) => i.key === key);
  return (
    <div className={cx('ds-tabs', className)} style={style}>
      <div className="ds-tabs-nav">
        {items.map((i) => (
          <div
            key={i.key}
            className={cx('ds-tab', i.key === key && 'active', i.disabled && 'disabled')}
            onClick={() => !i.disabled && set(i.key)}
          >
            {i.label}
          </div>
        ))}
      </div>
      <div>{current?.children}</div>
    </div>
  );
}
(TabsImpl as any).TabPane = function TabPane({ children }: any) { return <>{children}</>; };

export function Popconfirm({ title, description, onConfirm, onCancel, okText = 'Yes', cancelText = 'No', children, disabled, placement, icon }: any) {
  const [open, setOpen] = useState(false);
  const handleChildClick = (e: any) => {
    e.stopPropagation();
    if (!disabled) setOpen((o) => !o);
  };
  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement={placement || 'bottom'}
      dropdownRender={() => (
        <div style={{ padding: 14, minWidth: 240 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <span style={{ color: 'var(--warning)', marginTop: 2 }}>{icon ?? <ExclamationCircleOutlined />}</span>
            <div>
              <div style={{ fontWeight: 600, marginBottom: description ? 4 : 0 }}>{title}</div>
              {description && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{description}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <Button size="small" onClick={(e) => { e.stopPropagation(); onCancel?.(); setOpen(false); }}>{cancelText}</Button>
            <Button size="small" type="primary" danger onClick={(e) => { e.stopPropagation(); onConfirm?.(); setOpen(false); }}>{okText}</Button>
          </div>
        </div>
      )}
    >
      <span onClick={handleChildClick}>{children}</span>
    </Dropdown>
  );
}

export function Tooltip({ title, children, placement }: any) {
  if (!title) return <>{children}</>;
  return isValidElement(children) ? cloneElement(children as any, { title: typeof title === 'string' ? title : undefined }) : <>{children}</>;
}

export function Popover({ content, title, children, trigger, placement }: any) {
  return (
    <Dropdown
      trigger={trigger || ['hover']}
      placement={placement || 'bottom'}
      dropdownRender={() => (
        <div style={{ padding: 12, minWidth: 200 }}>
          {title && <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>}
          <div>{content}</div>
        </div>
      )}
    >
      {children}
    </Dropdown>
  );
}

export function Segmented({ options = [], value, defaultValue, onChange, block, size, className, style }: { options?: any[]; value?: any; defaultValue?: any; onChange?: (v: any) => void; block?: boolean; size?: string; className?: string; style?: CSSProperties }) {
  const isControlled = value !== undefined;
  const [inner, setInner] = useState(defaultValue);
  const v = isControlled ? value : inner;
  const set = (nv: any) => { if (!isControlled) setInner(nv); onChange?.(nv); };
  return (
    <div className={cx('ds-tabs-pill', className)} style={{ display: block ? 'flex' : 'inline-flex', ...style }}>
      {options.map((opt: any) => {
        const o = typeof opt === 'string' || typeof opt === 'number' ? { label: opt, value: opt } : opt;
        return (
          <div
            key={String(o.value)}
            className={cx('ds-tab', v === o.value && 'active')}
            onClick={() => !o.disabled && set(o.value)}
            style={{ flex: block ? 1 : undefined, textAlign: 'center' }}
          >
            {o.icon && <span style={{ marginInlineEnd: 6 }}>{o.icon}</span>}
            {o.label}
          </div>
        );
      })}
    </div>
  );
}

export function Breadcrumb({ items, separator = '/', className, style }: { items?: Array<{ title: ReactNode; href?: string; onClick?: any }>; separator?: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <nav className={className} style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text-secondary)', fontSize: 13, ...style }}>
      {items?.map((it, i) => (
        <Fragment key={i}>
          {i > 0 && <span style={{ color: 'var(--text-tertiary)' }}>{separator}</span>}
          {it.href ? <a href={it.href}>{it.title}</a> : <span>{it.title}</span>}
        </Fragment>
      ))}
    </nav>
  );
}

function ListImpl({ dataSource = [], renderItem, header, footer, bordered, split = true, size, locale, className, style, loading, itemLayout }: any) {
  return (
    <div className={cx('ds-list', className)} style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-md)', border: bordered ? '1px solid var(--grey-200)' : 0, ...style }}>
      {header && <div style={{ padding: 12, borderBottom: '1px solid var(--grey-100)', fontWeight: 600 }}>{header}</div>}
      {loading ? <Spin /> : dataSource.length === 0 ? <Empty description={locale?.emptyText} /> : (
        dataSource.map((item: any, i: number) => (
          <div key={i} style={{ padding: 12, borderBottom: split && i !== dataSource.length - 1 ? '1px solid var(--grey-100)' : 0 }}>
            {renderItem?.(item, i)}
          </div>
        ))
      )}
      {footer && <div style={{ padding: 12, borderTop: '1px solid var(--grey-100)' }}>{footer}</div>}
    </div>
  );
}
(ListImpl as any).Item = function ListItem({ children, actions, extra, style }: any) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', ...style }}>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
      {extra}
    </div>
  );
};
(ListImpl as any).Item.Meta = function ListItemMeta({ title, description, avatar }: any) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {avatar}
      <div style={{ minWidth: 0 }}>
        {title && <div style={{ fontWeight: 600 }}>{title}</div>}
        {description && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{description}</div>}
      </div>
    </div>
  );
};

/* ========================================================================
   Default export + types
   ======================================================================== */

// typed static re-export for Select
export const Select = SelectImpl as typeof SelectImpl & {
  Option: any;
};

// typed static re-export for Card
export const Card = CardImpl as typeof CardImpl & {
  Meta: any;
  Grid: any;
};

// typed static re-export for Tag
export const Tag = TagImpl as typeof TagImpl & {
  CheckableTag: any;
};

// typed static re-export for Empty
export const Empty = EmptyImpl as typeof EmptyImpl & {
  PRESENTED_IMAGE_DEFAULT: any;
  PRESENTED_IMAGE_SIMPLE: any;
};

// typed static re-export for Space
export const Space = SpaceImpl as typeof SpaceImpl & {
  Compact: any;
};

// typed static re-export for Layout
export const Layout = LayoutImpl as typeof LayoutImpl & {
  Sider: any;
  Header: any;
  Content: any;
  Footer: any;
};

// typed static re-export for Dropdown
export const Dropdown = DropdownImpl as typeof DropdownImpl & {
  Button: any;
};

// typed static re-export for Modal
export const Modal = ModalImpl as typeof ModalImpl & {
  confirm: any;
  info: any;
  success: any;
  error: any;
  warning: any;
  destroyAll: any;
};

// typed static re-export for Avatar
export const Avatar = AvatarImpl as typeof AvatarImpl & {
  Group: any;
};

// typed static re-export for Descriptions
export const Descriptions = DescriptionsImpl as typeof DescriptionsImpl & {
  Item: any;
};

// typed static re-export for Image
export const Image = ImageImpl as typeof ImageImpl & {
  PreviewGroup: any;
};

// typed static re-export for Badge
export const Badge = BadgeImpl as typeof BadgeImpl & {
  Ribbon: any;
};

// typed static re-export for Tabs
export const Tabs = TabsImpl as typeof TabsImpl & {
  TabPane: any;
};

// typed static re-export for List
export const List = ListImpl as typeof ListImpl & {
  Item: any;
};

// typed static re-export for DatePicker
export const DatePicker = DatePickerImpl as typeof DatePickerImpl & {
  RangePicker: any;
};

export default {
  Button, Input, InputNumber, Select, Switch, Checkbox, Radio, Card, Tag, Alert, Spin, Empty,
  Typography, Space, Flex, Row, Col, Divider, Layout, Menu, Dropdown, Modal, Drawer, Avatar,
  Descriptions, Image, Table, DatePicker, Form, App, ConfigProvider, theme, message, notification,
  Statistic, Progress, Badge, Skeleton, Tabs, Popconfirm, Tooltip, Popover, Segmented, Breadcrumb, List,
};
